﻿import {IProgress, isNullOrEmpty} from "@2d-front-monorepo/core";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {CreateOrSelectDriverModel} from "../components/CreateOrSelectDriverComponent";
import {CreateOrSelectResidencyModel} from "../components/CreateOrSelectResidencyComponent";
import {CreateOrSelectVehicleModel} from "../components/CreateOrSelectVehicleComponent";
import AttachmentType from "../enums/AttachmentType";
import IPermitsHttpClient from "../http/IPermitsHttpClient";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {Address} from "../types/Address";
import {CreatePermitArguments} from "../types/arguments/CreatePermitArguments";
import {FormDriverData, FormDriverId} from "../types/arguments/FormDriver";
import {FormHolder} from "../types/arguments/FormHolder";
import {FormResidencyData, FormResidencyId} from "../types/arguments/FormResidency";
import {FormVehicleData, FormVehicleId} from "../types/arguments/FormVehicle";
import {BillingData} from "../types/BillingData";
import {CreatePermitCommand, UpdateHolderModel} from "../types/CreatePermitCommand";
import {CreatePermitResult} from "../types/CreatePermitResult";
import {FormAttachmentData} from "../types/FormAttachmentData";
import {PhoneNumber} from "../types/PhoneNumber";
import countAttachments from "./countAttachments";
import {ProgressReporter} from "./progressReporter";
import reportProgress from "./reportProgress";
import {toFormAttachments} from "./toFormAttachments";
import {updatePaymentMethod} from "./updatePaymentMethod";

function countIAttachments(component: ICreateComponentAttachments): number {
	if (component.attachments == null) return 0;
	return countAttachments(component.attachments);
}

function countAllAttachments(state: CreatePermitCommand): number {
	return countIAttachments(state.permit)
		+ countIAttachments(state.driver)
		+ countIAttachments(state.residency)
		+ state.vehicles.reduce(
			function (group, vehicles) {
				return group + vehicles.reduce(function (accumulator, vehicle) {
					return accumulator + countIAttachments(vehicle)
				}, 0);
			}, 0);
}

function normalizePhone(phone: PhoneNumber | undefined): PhoneNumber | undefined {
	if (phone == null || isNullOrEmpty(phone.phone)) {
		return undefined;
	}

	return phone;
}

export interface AttachmentFormData {
	data: FormData;
	type: AttachmentType;
}

async function toFormHolder(httpClient: IPermitsHttpClient, holder: UpdateHolderModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormHolder | undefined> {
	const attachments = await toFormAttachments(httpClient, holder.attachments, progress, reporter);
	const holderArguments = holder.arguments;
	return {
		type: holderArguments.type,
		givenName: isNullOrEmpty(holderArguments.givenName) ? undefined : holderArguments.givenName,
		familyName: isNullOrEmpty(holderArguments.familyName) ? undefined : holderArguments.familyName,
		notificationsEmail: isNullOrEmpty(holderArguments.notificationsEmail) ? undefined : holderArguments.notificationsEmail,
		cellPhone: normalizePhone(holderArguments.cellPhone),
		homePhone: normalizePhone(holderArguments.homePhone),
		workPhone: normalizePhone(holderArguments.workPhone),
		company: holderArguments.company == null || (isNullOrEmpty(holderArguments.company.name) && isNullOrEmpty(holderArguments.company.taxId)) ? undefined : holderArguments.company,

		attachments: attachments,
		customData: holder.arguments.customData
	}
}

async function toFormDriver(httpClient: IPermitsHttpClient, driver: CreateOrSelectDriverModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormDriverId | FormDriverData | undefined> {
	if (isNullOrEmpty(driver.driverId)) {
		return undefined;
	}

	const attachments = await toFormAttachments(httpClient, driver.attachments, progress, reporter);
	if (driver.driverId === "create") {
		return {
			fullName: driver.arguments.fullName,
			licenseNumber: driver.arguments.licenseNumber,
			attachments: attachments,
			customData: driver.arguments.customData
		}
	}

	return {
		id: driver.driverId,
		attachments: attachments,
		customData: driver.arguments.customData
	}
}

function isEmptyAddress(value: Address): boolean {
	return isNullOrEmpty(value.street) && isNullOrEmpty(value.streetNumber);
}

async function toFormResidency(httpClient: IPermitsHttpClient, residency: CreateOrSelectResidencyModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormResidencyId | FormResidencyData | undefined> {
	if (isNullOrEmpty(residency.residencyId) || (residency.residencyId === "create" && isEmptyAddress(residency.arguments.address))) {
		return undefined;
	}

	const attachments = await toFormAttachments(httpClient, residency.attachments, progress, reporter);
	if (residency.residencyId === "create") {
		const address = residency.arguments.address;
		return {
			address: {
				street: address.street,
				streetNumber: address.streetNumber,
				unit: address.unit == null ? undefined : {
					unit: address.unit.unit,
					designator: address.unit.designator
				},
				city: address.city,
				state: address.state,
				postalCode: address.postalCode
			},
			uses: residency.arguments.uses,
			suffix: residency.arguments.suffix,
			zoneId: residency.arguments.zoneId,
			attachments: attachments,
			customData: residency.arguments.customData
		}
	}

	return {
		id: residency.residencyId,
		attachments: attachments,
		customData: residency.arguments.customData
	}
}

async function toFormVehicle(httpClient: IPermitsHttpClient, vehicle: CreateOrSelectVehicleModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormVehicleId | FormVehicleData> {
	const attachments = await toFormAttachments(httpClient, vehicle.attachments, progress, reporter);
	if (vehicle.vehicleId === "create") {
		return {
			plate: vehicle.arguments.plate,
			type: vehicle.arguments.type,
			data: vehicle.arguments.data,
			attachments: attachments,
			customData: vehicle.arguments.customData
		}
	}

	return {
		id: vehicle.vehicleId,
		attachments: attachments,
		customData: vehicle.arguments.customData
	}
}

function updateBillingData(billingData: BillingData | undefined, permitAttachments: FormAttachmentData[]): BillingData | undefined {
	if (billingData == null) {
		return billingData;
	}

	updatePaymentMethod(billingData.paymentMethod, permitAttachments);
	return billingData;
}

export async function createPermitFromCommand(httpClient: IPermitsHttpClient, state: CreatePermitCommand, reporter?: ProgressReporter | undefined): Promise<CreatePermitResult> {
	const progress: IProgress = {current: 0, total: countAllAttachments(state), label: ""};

	const [holder, driver, residency, vehicles, permitAttachments] = await Promise.all([
		toFormHolder(httpClient, state.holder, progress, reporter),
		toFormDriver(httpClient, state.driver, progress, reporter),
		toFormResidency(httpClient, state.residency, progress, reporter),
		Promise.all(state.vehicles.map(function (x) {
			return Promise.all(x.filter(function (x) {
				return !isNullOrEmpty(x.vehicleId);
			}).map(function (vehicle: CreateOrSelectVehicleModel) {
				return toFormVehicle(httpClient, vehicle, progress, reporter);
			}));
		})),
		toFormAttachments(httpClient, state.permit.attachments, progress, reporter)
	]);

	const args: CreatePermitArguments = {
		tariffId: state.tariffId,
		category: state.category,
		parkingType: state.parkingType,

		holder,
		driver,
		residency,

		permit: {
			startDate: state.permit.arguments.startDate,
			endDate: state.permit.arguments.endDate,
			branch: state.permit.arguments.branch,
			billingData: updateBillingData(state.permit.arguments.billingData, permitAttachments),
			vehicles,
			attachments: permitAttachments,
			customData: state.permit.arguments.customData,
			geolocation: undefined,
			autoRenew: state.permit.arguments.autoRenew
		},

		customData: state.customData
	}

	progress.label = PermitTranslations.create_permit__create_permit;
	reportProgress(progress, reporter);
	return httpClient.permits.create(args);
}
